import { MediaImage } from '@/lib/mediaDisplay';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useRef, useEffect, useCallback } from 'react';
import { retainedMediaDeleteMessage, useMedia, useUploadMedia, useDeleteMedia, useDeleteMediaBulk, useRenameMedia, useSite, useStorageConnections, mediaApi, contentKeys, MediaFolder, useStaticFiles, useUploadStaticFile, useDeleteStaticFile } from '@wordpuppi/shared';
import type { StaticFile, Media, MediaKind } from '@wordpuppi/shared';
import { ImageResizeDialog } from '@/components/ImageResizeDialog';
import { MediaLightbox } from '@/components/MediaLightbox';
import { toast } from 'sonner';
import { copyText } from '@/lib/clipboard';
import { confirmDialog } from '@/lib/dialog';
import { Upload, Trash2 } from 'lucide-react';
import { mediaIcon, mediaTypeLabel } from '@/lib/mediaIcon';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useBaxterChanged, useBaxterEffects } from '@/lib/baxterEffects';
import { notifyMediaChange } from '@/lib/mediaEffects';
import { useTurnHighlight } from '@/lib/useTurnHighlight';
import { UnderlineTabs } from '@/components/sub-components/UnderlineTabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StorageConfigModal } from '@/components/StorageConfigModal';
import { pickFilesDesktop } from '@/utils/desktopActions';

// AB#635: accept attr for the media uploader, shared by the input and the
// desktop pick interceptor so both offer identical file types.
// AB#662: portfolio document types (pdf/word/excel/markdown/txt/csv) too.
// Fonts are media now, so webfont files upload here too.
const MEDIA_ACCEPT = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.md,.txt,.csv,.woff,.woff2,.ttf,.otf';

export const Route = createFileRoute('/sites/$siteSlug/media')({
  component: MediaPage,
});

type CardSize = 'XS' | 'S' | 'M' | 'L';
type ViewMode = 'grid' | 'list';

const gridClasses: Record<CardSize, string> = {
  XS: 'grid-cols-4 md:grid-cols-6 lg:grid-cols-10 gap-2',
  S: 'grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-3',
  M: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4',
  L: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5',
};

const KIND_OPTIONS: { value: MediaKind | 'all'; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'image', label: 'Images' },
  { value: 'document', label: 'Documents' },
  { value: 'font', label: 'Fonts' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
];

const FOLDER_TABS = [
  { value: 'site' as const, label: 'Site Media' },
  { value: 'pages' as const, label: 'Pages' },
  { value: 'posts' as const, label: 'Posts' },
  { value: 'baxter' as const, label: 'Baxter' },
  { value: 'root' as const, label: 'All Media' },
];

// Quiet selected treatment for segmented controls (bordered group, never amber-filled).
const segmentColors = (active: boolean) =>
  active ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground';

type TopView = 'media' | 'static';

function MediaPage() {
  const { siteSlug } = Route.useParams();
  const [topView, setTopView] = useState<TopView>('media');
  const { data: siteData } = useSite(siteSlug);
  const { data: connectionsData } = useStorageConnections();

  const storageLabel = (() => {
    const site = siteData?.data;
    if (!site) return '';
    if (site.storage_connection_id && connectionsData?.data) {
      const conn = connectionsData.data.find((c) => c.id === site.storage_connection_id);
      if (conn) return conn.name;
    }
    const labels: Record<string, string> = { local: 'Local Storage', s3: 'S3', supabase: 'Supabase' };
    return labels[site.storage_type] ?? site.storage_type;
  })();

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-semibold">
          Media
          {storageLabel && <span className="text-sm text-muted-foreground font-normal ml-2">{storageLabel}</span>}
        </h1>
        <TopViewToggle topView={topView} onTopViewChange={setTopView} />
      </div>
      {topView === 'media'
        ? <MediaView siteSlug={siteSlug} />
        : <StaticFilesView siteSlug={siteSlug} />}
    </div>
  );
}

// ============================================================================
// Top-level view toggle
// ============================================================================

function TopViewToggle({ topView, onTopViewChange }: { topView: TopView; onTopViewChange: (v: TopView) => void }) {
  return (
    <div className="flex border rounded-md">
      <button
        onClick={() => onTopViewChange('media')}
        className={cn(
          'min-w-[5.5rem] px-4 py-1.5 min-h-11 md:min-h-0 text-sm font-medium transition-colors text-center',
          segmentColors(topView === 'media')
        )}
      >
        Media
      </button>
      <button
        onClick={() => onTopViewChange('static')}
        className={cn(
          'min-w-[5.5rem] px-4 py-1.5 min-h-11 md:min-h-0 text-sm font-medium transition-colors text-center',
          segmentColors(topView === 'static')
        )}
      >
        Static
      </button>
    </div>
  );
}

// ============================================================================
// Media View (existing)
// ============================================================================

function MediaView({ siteSlug }: { siteSlug: string }) {
  const [page, setPage] = useState(1);
  const [folder, setFolder] = useState<MediaFolder | 'site'>('site');
  const mediaTabs = useBaxterEffects((s) => s.mediaTabs);
  const acknowledgeMediaTab = useBaxterEffects((s) => s.acknowledgeMediaTab);
  const activeTabUpdate = mediaTabs[folder];
  useEffect(() => {
    const at = activeTabUpdate;
    if (!at) return;
    const timer = setTimeout(() => acknowledgeMediaTab(siteSlug, folder, at), 1600);
    return () => clearTimeout(timer);
  }, [siteSlug, folder, activeTabUpdate, acknowledgeMediaTab]);
  const [kind, setKind] = useState<MediaKind | 'all'>('all');
  const [cardSize, setCardSize] = useState<CardSize>('M');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [resizeItem, setResizeItem] = useState<Media | null>(null);
  const [viewItem, setViewItem] = useState<Media | null>(null);
  const queryClient = useQueryClient();

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [storageModalOpen, setStorageModalOpen] = useState(false);

  // Usage check state for delete dialog
  const [usageData, setUsageData] = useState<Record<string, Array<{ type: string; id: string; title: string }>> | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(false);
  const [usageAttempt, setUsageAttempt] = useState(0);
  const [usageCheckedKey, setUsageCheckedKey] = useState<string | null>(null);

  // IDs pending deletion (for single-item delete via dialog)
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string> | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const usageKey = JSON.stringify([siteSlug, [...(pendingDeleteIds ?? selectedIds)].sort()]);

  // Only filter by folder if not 'root' (root shows all)
  const { data, isLoading, error } = useMedia(siteSlug, {
    page,
    per_page: 24,
    folder: folder === 'pages' || folder === 'posts' ? folder : undefined,
    origin: folder === 'site' || folder === 'baxter' ? folder : undefined,
    kind: kind === 'all' ? undefined : kind,
  });
  const uploadMedia = useUploadMedia(siteSlug, folder === 'site' ? 'root' : folder);
  const deleteMedia = useDeleteMedia(siteSlug);
  const deleteMediaBulk = useDeleteMediaBulk(siteSlug);
  const renameMedia = useRenameMedia(siteSlug);
  // Baxter imports/resizes/screenshots land here — flash the new tiles.
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current items for keyboard/selection operations
  const items = data?.data ?? [];

  // Clear selection when changing pages, folders or the type filter
  useEffect(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, [page, folder, kind]);

  // Keyboard handler for delete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.size > 0) {
        e.preventDefault();
        setDeleteDialogOpen(true);
      }

      // Escape to clear selection
      if (e.key === 'Escape') {
        setSelectedIds(new Set());
        setLastSelectedId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds.size]);

  // Fetch usage data when delete dialog opens
  useEffect(() => {
    setDeleteError(null);
    if (!deleteDialogOpen) {
      setUsageData(null);
      setUsageCheckedKey(null);
      setUsageError(false);
      setUsageLoading(false);
      setConfirmText('');
      return;
    }

    const idsToCheck = pendingDeleteIds ?? selectedIds;
    if (idsToCheck.size === 0) return;

    let cancelled = false;
    setUsageData(null);
    setUsageCheckedKey(null);
    setUsageError(false);
    setUsageLoading(true);
    mediaApi.checkUsage(siteSlug, [...idsToCheck]).then((res) => {
      if (res.success !== true || !res.data || typeof res.data !== 'object' || Array.isArray(res.data)
        || Object.values(res.data).some((refs) => !Array.isArray(refs) || refs.some((ref) => !ref || typeof ref.id !== 'string' || typeof ref.type !== 'string' || typeof ref.title !== 'string'))) {
        throw new Error('Incomplete media usage response');
      }
      if (!cancelled) {
        setUsageData(res.data);
        setUsageCheckedKey(usageKey);
        setUsageLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setUsageError(true);
        setUsageLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [deleteDialogOpen, siteSlug, usageKey, usageAttempt]);

  const handleFolderChange = (newFolder: MediaFolder | 'site') => {
    const at = mediaTabs[newFolder];
    if (at) acknowledgeMediaTab(siteSlug, newFolder, at);
    setFolder(newFolder);
    setPage(1);
  };

  const handleKindChange = (newKind: MediaKind | 'all') => {
    setKind(newKind);
    setPage(1);
  };

  const handleItemClick = useCallback((id: string, event: React.MouseEvent) => {
    // Don't select if clicking menu button or during rename
    if (editingId) return;

    event.preventDefault();

    setSelectedIds(prev => {
      const next = new Set(prev);

      if (event.shiftKey && lastSelectedId && items.length > 0) {
        // Range selection
        const currentIndex = items.findIndex(item => item.id === id);
        const lastIndex = items.findIndex(item => item.id === lastSelectedId);

        if (currentIndex !== -1 && lastIndex !== -1) {
          const start = Math.min(currentIndex, lastIndex);
          const end = Math.max(currentIndex, lastIndex);

          for (let i = start; i <= end; i++) {
            next.add(items[i].id);
          }
        }
      } else {
        // Toggle selection
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }

      return next;
    });

    setLastSelectedId(id);
  }, [editingId, lastSelectedId, items]);

  const selectAll = () => {
    setSelectedIds(new Set(items.map(item => item.id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  };

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    let successCount = 0;
    for (const file of files) {
      try {
        const uploaded = await uploadMedia.mutateAsync(file);
        if (uploaded.data) void notifyMediaChange(queryClient, siteSlug, [uploaded.data], 'added').catch(() => {});
        successCount++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) =>
    uploadFiles(Array.from(e.target.files ?? []));

  const handleDelete = (id: string, _filename: string) => {
    setPendingDeleteIds(new Set([id]));
    setMenuOpenId(null);
    setDeleteDialogOpen(true);
  };

  const [removingAfterVersions, setRemovingAfterVersions] = useState(false);

  const handleConfirmDelete = async () => {
    const idsToDelete = pendingDeleteIds ?? selectedIds;
    if (idsToDelete.size === 0 || usageLoading || usageError || usageData === null || usageCheckedKey !== usageKey) return;
    if (Object.values(usageData).some((refs) => refs.length > 0) && confirmText !== 'Break it') return;

    const remove = async (afterVersions: boolean) => {
      if (idsToDelete.size === 1) {
        const id = [...idsToDelete][0];
        const result = afterVersions ? await mediaApi.delete(siteSlug, id, { afterVersions }) : await deleteMedia.mutateAsync(id);
        if (!result.success) throw new Error('Deletion was not confirmed. Refresh the library before trying again.');
        toast.success(afterVersions ? 'Removed from the library' : 'File deleted');
      } else {
        const result = afterVersions ? await mediaApi.deleteBulk(siteSlug, [...idsToDelete], { afterVersions }) : await deleteMediaBulk.mutateAsync([...idsToDelete]);
        const count = result.data?.deleted_count;
        if (!result.success || typeof count !== 'number' || !Number.isInteger(count) || count < 0 || count > idsToDelete.size) throw new Error('Deletion was not confirmed. Refresh the library before trying again.');
        toast.success(`${count} file${count !== 1 ? 's' : ''} ${afterVersions ? 'removed from the library' : 'deleted'}`);
      }
      if (afterVersions) void queryClient.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
    };

    try {
      try {
        await remove(false);
      } catch (err) {
        if ((err as { data?: { code?: string } } | null)?.data?.code !== 'MEDIA_RETAINED_BY_VERSIONS') throw err;
        // AB#1038: versions reference files rather than copying them, so offer the safe way forward.
        const n = (err as { data?: { details?: { version_count?: number } } }).data?.details?.version_count;
        const one = idsToDelete.size === 1;
        const who = typeof n === 'number' ? `${n} saved version${n === 1 ? '' : 's'} of your site` : 'Saved versions of your site';
        const ok = await confirmDialog(
          `${who} still use${n === 1 ? 's' : ''} ${one ? 'this file' : 'these files'}. Remove ${one ? 'it' : 'them'} from the library now? Those versions keep working, and WordPuppi deletes the stored file once none of them use it.`,
          { title: 'Used by saved versions', confirmLabel: 'Remove from library', destructive: true },
        );
        if (!ok) {
          setDeleteError(retainedMediaDeleteMessage(err));
          return;
        }
        setRemovingAfterVersions(true);
        try {
          await remove(true);
        } finally {
          setRemovingAfterVersions(false);
        }
      }
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (const id of idsToDelete) next.delete(id);
        return next;
      });
      setLastSelectedId(null);
      setDeleteDialogOpen(false);
      setPendingDeleteIds(null);
    } catch (err) {
      const message = retainedMediaDeleteMessage(err) ?? (err instanceof Error ? err.message : 'Deletion was not confirmed. Refresh the library before trying again.');
      setDeleteError(message);
      toast.error(message);
    }
  };

  const copyUrl = async (url: string) => {
    if (await copyText(url)) toast.success('URL copied to clipboard');
    setMenuOpenId(null);
  };

  const startRename = (id: string, currentName: string) => {
    setEditingId(id);
    setEditName(currentName);
    setMenuOpenId(null);
  };

  const handleRename = async (id: string) => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      await renameMedia.mutateAsync({ id, name: editName.trim() });
      toast.success('File renamed');
      setEditingId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rename file');
    }
  };

  const handlePromote = async (id: string) => {
    setMenuOpenId(null);
    try {
      await mediaApi.promote(siteSlug, id);
      const promoted = await mediaApi.get(siteSlug, id).catch(() => null);
      void notifyMediaChange(queryClient, siteSlug, promoted?.data ? [promoted.data] : []).catch(() => {});
      toast.success('Moved to Site Media');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not move media');
    }
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditName('');
  };

  const toggleMenu = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpenId(menuOpenId === id ? null : id);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const flash = useTurnHighlight(data?.data?.map((row) => ({ id: row.id, name: row.original_name || row.filename, url: row.url, alt: row.alt_text || row.filename, type: mediaTypeLabel(row.mime_type, row.original_name || row.filename), ...(viewMode === 'list' ? { size: formatFileSize(row.size_bytes), date: formatDate(row.created_at) } : {}) })), useBaxterChanged('media'));

  return (
    <div>
      <div className="flex justify-end items-center mb-4">
        <div className="flex flex-wrap items-center justify-end gap-3">
          {/* View mode toggle */}
          <div className="flex border rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'px-3 py-1.5 min-h-11 md:min-h-0 text-sm font-medium transition-colors',
                segmentColors(viewMode === 'grid')
              )}
              title="Grid view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'px-3 py-1.5 min-h-11 md:min-h-0 text-sm font-medium transition-colors',
                segmentColors(viewMode === 'list')
              )}
              title="List view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
          </div>

          {/* Size selector (only for grid view) */}
          {viewMode === 'grid' && (
            <div className="flex border rounded-md">
              {(['XS', 'S', 'M', 'L'] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => setCardSize(size)}
                  className={cn(
                    'px-3 py-1.5 min-h-11 md:min-h-0 text-sm font-medium transition-colors',
                    segmentColors(cardSize === size)
                  )}
                >
                  {size}
                </button>
              ))}
            </div>
          )}

          <Button asChild>
            <label className="cursor-pointer">
              Upload to {FOLDER_TABS.find(f => f.value === folder)?.label}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={MEDIA_ACCEPT}
                onClick={(e) => pickFilesDesktop(e, { multiple: true, accept: MEDIA_ACCEPT }, uploadFiles)}
                onChange={handleUpload}
                className="hidden"
              />
            </label>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0 basis-full sm:basis-0">
          <div className="min-w-0 overflow-x-auto"><UnderlineTabs tabs={FOLDER_TABS.map((tab) => ({ ...tab, updatedAt: mediaTabs[tab.value] }))} activeTab={folder} onTabChange={handleFolderChange} /></div>
          {folder === 'baxter' && <p className="mt-2 text-sm text-muted-foreground">Free-plan Baxter pictures expire after 30 minutes. Move them to Site Media to keep them. Pictures saved in content are kept automatically.</p>}
        </div>
        <Select value={kind} onValueChange={(v) => handleKindChange(v as MediaKind | 'all')}>
          <SelectTrigger aria-label="Filter by file type" size="sm" className="mb-4 w-36">
            {/* base-ui Select.Value renders the raw value, so map it to a label */}
            <SelectValue>
              {(v: unknown) => KIND_OPTIONS.find((o) => o.value === v)?.label ?? 'All types'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {KIND_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Selection Toolbar - only shown once at least one item is selected */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={selectAll}>
              Select All
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {uploadMedia.isPending && (
        <div className="flex items-center justify-center py-4 text-muted-foreground">
          Uploading...
        </div>
      )}

      {isLoading ? (
        viewMode === 'grid' ? (
          <div className={cn('grid', gridClasses[cardSize])}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="aspect-square rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        )
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">Error loading media</div>
      ) : data?.data?.length === 0 ? (
        <div className="border border-dashed rounded-lg py-16 px-6 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Upload className="h-5 w-5" />
          </div>
          <p className="text-lg font-medium">
            {kind !== 'all'
              ? `No ${KIND_OPTIONS.find(k => k.value === kind)?.label.toLowerCase()} in ${FOLDER_TABS.find(f => f.value === folder)?.label} yet`
              : folder !== 'root'
                ? `No media in ${FOLDER_TABS.find(f => f.value === folder)?.label} yet`
                : 'Your media library is empty'}
          </p>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            Upload images, videos, or documents here to use across your posts and pages.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              Upload files
            </Button>
            <Button variant="ghost" onClick={() => setStorageModalOpen(true)}>
              Configure storage
            </Button>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <>
          <div className={cn('grid', gridClasses[cardSize])}>
            {data?.data?.map((item) => (
              <Card
                key={item.id}
                data-changed={flash.has(item.id) ? 'true' : undefined}
                className={cn(
                  'media-tile overflow-hidden cursor-pointer transition-[border-color,box-shadow]',
                  selectedIds.has(item.id) && 'ring-2 ring-primary ring-offset-2',
                  flash.has(item.id) && 'baxter-changed'
                )}
                onClick={(e) => handleItemClick(item.id, e)}
              >
                {item.mime_type.startsWith('image/') ? (
                  <div className="aspect-square bg-muted relative overflow-hidden">
                    <MediaImage
                scopeKey={siteSlug}
                      src={item.url}
                      alt={item.alt_text || item.filename}
                      className="w-full h-full object-cover text-transparent"
                    />
                    {selectedIds.has(item.id) && (
                      <div className="absolute top-2 left-2 w-5 h-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="aspect-square bg-muted flex flex-col items-center justify-center gap-2 relative">
                    {(() => {
                      const Icon = mediaIcon(item.mime_type, item.original_name || item.filename);
                      return <Icon className="w-10 h-10 text-muted-foreground" strokeWidth={1.5} />;
                    })()}
                    <span className="text-xs text-muted-foreground">
                      {mediaTypeLabel(item.mime_type, item.original_name || item.filename)}
                    </span>
                    {selectedIds.has(item.id) && (
                      <div className="absolute top-2 left-2 w-5 h-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}
                  </div>
                )}
                <CardContent className="p-3">
                  {editingId === item.id ? (
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      <Input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(item.id);
                          if (e.key === 'Escape') cancelRename();
                        }}
                        autoFocus
                        className="h-8 text-sm"
                      />
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => handleRename(item.id)}
                          disabled={renameMedia.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={cancelRename}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="text-sm truncate flex-1"
                        title={item.original_name || item.filename}
                      >
                        {item.original_name || item.filename}
                      </span>
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => toggleMenu(item.id, e)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="1" />
                            <circle cx="12" cy="5" r="1" />
                            <circle cx="12" cy="19" r="1" />
                          </svg>
                        </Button>
                        {menuOpenId === item.id && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); }}
                            />
                            <div className="absolute right-0 bottom-full mb-1 z-50 min-w-30 bg-popover border rounded-md shadow-md py-1 origin-bottom-right transition-[opacity,scale] duration-(--duration-popover) ease-out starting:scale-95 starting:opacity-0 motion-reduce:starting:scale-100">
                              {item.mime_type.startsWith('image/') && (
                                <button
                                  className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                                  onClick={(e) => { e.stopPropagation(); setViewItem(item); setMenuOpenId(null); }}
                                >
                                  View
                                </button>
                              )}
                              {item.origin === 'baxter' && (
                                <button className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent" onClick={(e) => { e.stopPropagation(); void handlePromote(item.id); }}>
                                  Move to Site Media
                                </button>
                              )}
                              <button
                                className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                                onClick={(e) => { e.stopPropagation(); startRename(item.id, item.original_name || item.filename); }}
                              >
                                Rename
                              </button>
                              {item.mime_type.startsWith('image/') && (
                                <button
                                  className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                                  onClick={(e) => { e.stopPropagation(); setResizeItem(item); setMenuOpenId(null); }}
                                >
                                  Resize
                                </button>
                              )}
                              <button
                                className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                                onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }}
                              >
                                Copy URL
                              </button>
                              <button
                                className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent text-destructive"
                                onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.original_name || item.filename); }}
                                disabled={deleteMedia.isPending}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {data?.meta && data.meta.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-muted-foreground">
                Page {page} of {data.meta.total_pages}
              </span>
              <Button
                variant="secondary"
                disabled={page >= data.meta.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      ) : (
        /* List View */
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead className="w-16">Preview</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-24">Size</TableHead>
                <TableHead className="w-32">Date</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data?.map((item) => (
                <TableRow
                  key={item.id}
                  data-changed={flash.has(item.id) ? 'true' : undefined}
                  className={cn(
                    'cursor-pointer',
                    selectedIds.has(item.id) && 'bg-primary/10',
                    flash.has(item.id) && 'baxter-changed'
                  )}
                  onClick={(e) => handleItemClick(item.id, e)}
                >
                  <TableCell>
                    <div
                      className={cn(
                        'w-5 h-5 rounded border-2 flex items-center justify-center',
                        selectedIds.has(item.id)
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-muted-foreground/30'
                      )}
                    >
                      {selectedIds.has(item.id) && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.mime_type.startsWith('image/') ? (
                      <MediaImage
                scopeKey={siteSlug}
                        src={item.url}
                        alt={item.alt_text || item.filename}
                        className="w-10 h-10 object-cover rounded text-transparent"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                        {(() => {
                          const Icon = mediaIcon(item.mime_type, item.original_name || item.filename);
                          return <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />;
                        })()}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === item.id ? (
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(item.id);
                            if (e.key === 'Escape') cancelRename();
                          }}
                          autoFocus
                          className="h-8 text-sm"
                        />
                        <Button
                          size="sm"
                          className="h-8"
                          onClick={() => handleRename(item.id)}
                          disabled={renameMedia.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-8"
                          onClick={cancelRename}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <span className="truncate" title={item.original_name || item.filename}>
                        {item.original_name || item.filename}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {mediaTypeLabel(item.mime_type, item.original_name || item.filename)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatFileSize(item.size_bytes)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(item.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => toggleMenu(item.id, e)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="1" />
                          <circle cx="12" cy="5" r="1" />
                          <circle cx="12" cy="19" r="1" />
                        </svg>
                      </Button>
                      {menuOpenId === item.id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={(e) => { e.stopPropagation(); setMenuOpenId(null); }}
                          />
                          <div className="absolute right-0 top-full mt-1 z-50 min-w-30 bg-popover border rounded-md shadow-md py-1 origin-top-right transition-[opacity,scale] duration-(--duration-popover) ease-out starting:scale-95 starting:opacity-0 motion-reduce:starting:scale-100">
                            {item.mime_type.startsWith('image/') && (
                              <button
                                className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                                onClick={(e) => { e.stopPropagation(); setViewItem(item); setMenuOpenId(null); }}
                              >
                                View
                              </button>
                            )}
                            {item.origin === 'baxter' && (
                                <button className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent" onClick={(e) => { e.stopPropagation(); void handlePromote(item.id); }}>
                                  Move to Site Media
                                </button>
                              )}
                            <button
                              className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                              onClick={(e) => { e.stopPropagation(); startRename(item.id, item.original_name || item.filename); }}
                            >
                              Rename
                            </button>
                            {item.mime_type.startsWith('image/') && (
                              <button
                                className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                                onClick={(e) => { e.stopPropagation(); setResizeItem(item); setMenuOpenId(null); }}
                              >
                                Resize
                              </button>
                            )}
                            <button
                              className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent"
                              onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }}
                            >
                              Copy URL
                            </button>
                            <button
                              className="w-full px-3 py-1.5 text-sm text-left hover:bg-accent text-destructive"
                              onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.original_name || item.filename); }}
                              disabled={deleteMedia.isPending}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {data?.meta && data.meta.total_pages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-muted-foreground">
                Page {page} of {data.meta.total_pages}
              </span>
              <Button
                variant="secondary"
                disabled={page >= data.meta.total_pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setPendingDeleteIds(null);
      }}>
        <DialogContent>
          {(() => {
            const idsToDelete = pendingDeleteIds ?? selectedIds;
            const deleteCount = idsToDelete.size;
            const isDeleting = deleteMedia.isPending || deleteMediaBulk.isPending || removingAfterVersions;
            const usageEntries = usageData ? Object.entries(usageData).filter(([, refs]) => refs.length > 0) : [];
            const hasRefs = usageEntries.length > 0;
            const needsConfirmText = hasRefs && !usageLoading;

            return (
              <>
                <DialogHeader>
                  <DialogTitle>Delete {deleteCount} file{deleteCount !== 1 ? 's' : ''}?</DialogTitle>
                  <DialogDescription>
                    This action cannot be undone. The selected file{deleteCount !== 1 ? 's' : ''} will be permanently deleted.
                  </DialogDescription>
                </DialogHeader>

                {usageLoading ? (
                  <div className="text-sm text-muted-foreground py-2">Checking for references...</div>
                ) : usageError ? (
                  <div role="alert" className="space-y-2 py-2 text-sm">
                    <p>Could not verify where these files are used. Check again before deleting.</p>
                    <Button variant="secondary" onClick={() => { setUsageCheckedKey(null); setUsageAttempt((attempt) => attempt + 1); }}>Retry usage check</Button>
                  </div>
                ) : hasRefs ? (
                  <div className="space-y-3">
                    <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm space-y-3">
                      <p className="font-medium text-destructive">
                        Deleting {deleteCount !== 1 ? 'these files' : 'this file'} will break references in:
                      </p>
                      <div className="space-y-3">
                        {usageEntries.map(([mediaId, refs]) => {
                          const mediaItem = items.find(i => i.id === mediaId);
                          const isImage = mediaItem?.mime_type?.startsWith('image/');
                          return (
                            <div key={mediaId} className="space-y-1.5">
                              <div className="flex items-center gap-2">
                                {mediaItem && isImage ? (
                                  <MediaImage
                scopeKey={siteSlug}
                                    src={mediaItem.url}
                                    alt=""
                                    className="w-8 h-8 rounded object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0 text-xs">
                                    {mediaItem?.mime_type?.split('/')[1]?.slice(0, 3).toUpperCase() ?? '?'}
                                  </div>
                                )}
                                <span className="text-sm font-medium truncate">
                                  {mediaItem?.original_name || mediaItem?.filename || mediaId}
                                </span>
                              </div>
                              <ul className="list-disc pl-12 space-y-0.5">
                                {refs.map((ref) => (
                                  <li key={ref.id} className="text-sm">
                                    <a
                                      href={ref.type === 'post'
                                        ? `/sites/${siteSlug}/posts/${ref.id}`
                                        : `/sites/${siteSlug}/pages/${ref.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="underline hover:text-destructive"
                                    >
                                      {ref.title || 'Untitled'}
                                    </a>
                                    <span className="text-muted-foreground ml-1">({ref.type})</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground block mb-1.5">
                        Type <span className="font-semibold text-foreground">Break it</span> to confirm
                      </label>
                      <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Break it"
                        className="max-w-[200px]"
                        autoFocus
                      />
                    </div>
                  </div>
                ) : null}

                {deleteError && <p role="alert" className="text-sm text-destructive">{deleteError}</p>}
                <DialogFooter>
                  <Button
                    variant="secondary"
                    onClick={() => { setDeleteDialogOpen(false); setPendingDeleteIds(null); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting || usageLoading || usageError || usageData === null || usageCheckedKey !== usageKey || (needsConfirmText && confirmText !== 'Break it')}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Storage Configuration Modal */}
      <StorageConfigModal
        open={storageModalOpen}
        onOpenChange={setStorageModalOpen}
        siteSlug={siteSlug}
      />

      {/* Image Resize Dialog */}
      {resizeItem && (
        <ImageResizeDialog
          open={!!resizeItem}
          onOpenChange={(open) => { if (!open) setResizeItem(null); }}
          siteSlug={siteSlug}
          mediaId={resizeItem.id}
          currentWidth={resizeItem.width ?? 0}
          currentHeight={resizeItem.height ?? 0}
          imageUrl={resizeItem.url}
          onResized={() => {
            queryClient.invalidateQueries({ queryKey: contentKeys.media.all(siteSlug) });
            setResizeItem(null);
          }}
        />
      )}

      {/* Fullscreen zoom/pan lightbox */}
      {viewItem && (
        <MediaLightbox
          key={viewItem.id}
          src={viewItem.url}
          scopeKey={siteSlug}
          alt={viewItem.alt_text || viewItem.filename}
          filename={viewItem.original_name || viewItem.filename}
          onClose={() => setViewItem(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Static Files View
// ============================================================================

type StaticCategory = 'fonts' | 'css' | 'js' | 'other';

const STATIC_CATEGORIES: { value: StaticCategory; label: string; accept: string; subPath: string }[] = [
  { value: 'fonts', label: 'Fonts', accept: '.woff2,.woff,.ttf,.otf', subPath: 'fonts' },
  { value: 'css', label: 'CSS', accept: '.css', subPath: 'css' },
  { value: 'js', label: 'JS', accept: '.js,.map', subPath: 'js' },
  { value: 'other', label: 'Other', accept: '.svg,.json', subPath: '' },
];

function StaticFilesView({ siteSlug }: { siteSlug: string }) {
  const { data, isLoading, error } = useStaticFiles(siteSlug);
  const uploadStaticFile = useUploadStaticFile(siteSlug);
  const deleteStaticFile = useDeleteStaticFile(siteSlug);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<StaticCategory>('fonts');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeletePath, setPendingDeletePath] = useState<string | null>(null);

  const files: StaticFile[] = data?.data?.files ?? [];

  // Group files by category
  const grouped = files.reduce<Record<string, StaticFile[]>>((acc, file) => {
    const cat = file.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(file);
    return acc;
  }, {});

  const activeConfig = STATIC_CATEGORIES.find((c) => c.value === category)!;
  const activeFiles = grouped[category] ?? [];

  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) return;

    let successCount = 0;
    for (const file of files) {
      try {
        await uploadStaticFile.mutateAsync({ file, subPath: activeConfig.subPath || undefined });
        successCount++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) =>
    uploadFiles(Array.from(e.target.files ?? []));

  const handleDelete = (path: string) => {
    setPendingDeletePath(path);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeletePath) return;
    try {
      await deleteStaticFile.mutateAsync(pendingDeletePath);
      toast.success('File deleted');
      setDeleteDialogOpen(false);
      setPendingDeletePath(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete file');
    }
  };

  const copySnippet = async (templatePath: string) => {
    if (await copyText(templatePath)) toast.success('Template path copied');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex border rounded-md">
          {STATIC_CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={cn(
                'px-4 py-1.5 min-h-11 md:min-h-0 text-sm font-medium transition-colors',
                segmentColors(category === cat.value)
              )}
            >
              {cat.label}
              {(grouped[cat.value]?.length ?? 0) > 0 && (
                <span className="ml-1.5 text-xs opacity-70">{grouped[cat.value].length}</span>
              )}
            </button>
          ))}
        </div>
        <Button asChild>
          <label className="cursor-pointer">
            Upload {activeConfig.label}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={activeConfig.accept}
              onClick={(e) => pickFilesDesktop(e, { multiple: true, accept: activeConfig.accept }, uploadFiles)}
              onChange={handleUpload}
              className="hidden"
            />
          </label>
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Files upload to <code className="bg-muted px-1.5 py-0.5 rounded text-xs">static/{activeConfig.subPath || ''}</code> and are copied to the build output. Site files override theme files with the same name.
      </p>

      {uploadStaticFile.isPending && (
        <div className="flex items-center justify-center py-4 text-muted-foreground">
          Uploading...
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">Error loading static files</div>
      ) : activeFiles.length === 0 ? (
        <div className="border border-dashed rounded-lg py-16 px-6 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Upload className="h-5 w-5" />
          </div>
          <p className="text-lg font-medium">No {activeConfig.label.toLowerCase()} files yet</p>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            Upload {activeConfig.label.toLowerCase()} files here — they are copied into the build output and can override theme files.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => fileInputRef.current?.click()}>
            Upload {activeConfig.label}
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Path</TableHead>
              <TableHead className="w-24">Size</TableHead>
              <TableHead className="w-72">Template Snippet</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeFiles.map((file) => (
              <TableRow key={file.path}>
                <TableCell className="font-mono text-sm">{file.path}</TableCell>
                <TableCell className="text-muted-foreground">{formatFileSize(file.size_bytes)}</TableCell>
                <TableCell>
                  <button
                    onClick={() => copySnippet(file.template_path)}
                    className="text-xs font-mono bg-muted px-2 py-1 rounded hover:bg-muted/80 text-left truncate max-w-full block"
                    title="Click to copy"
                  >
                    {file.template_path}
                  </button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => handleDelete(file.path)}
                    disabled={deleteStaticFile.isPending}
                    title="Delete file"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setPendingDeletePath(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete static file?</DialogTitle>
            <DialogDescription>
              This will permanently delete <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{pendingDeletePath}</code>. Templates referencing this file will break.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setDeleteDialogOpen(false); setPendingDeletePath(null); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteStaticFile.isPending}
            >
              {deleteStaticFile.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
